import React, { useState, useEffect } from 'react';
import {
  SimpleForm,
  TextInput,
  NumberInput,
  DateInput,
  SelectInput,
  BooleanInput,
  required,
  useNotify,
  useRefresh,
  useDataProvider,
  useTranslate,
  Toolbar,
  SaveButton,
} from 'react-admin';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Grid,
  Box,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { EquipmentFileUpload } from '../EquipmentFileUpload';

const IncidentToolbar = () => (
  <Toolbar sx={{ 
    display: 'flex', 
    justifyContent: 'flex-end',
    padding: '16px 24px',
    borderTop: '1px solid #e0e0e0'
  }}>
    <SaveButton />
  </Toolbar>
);

interface IncidentDialogProps {
  open: boolean;
  onClose: () => void;
  equipmentId: number;
  recordId?: number;
  isEdit?: boolean;
}

export const IncidentDialog: React.FC<IncidentDialogProps> = ({ 
  open, 
  onClose, 
  equipmentId,
  recordId,
  isEdit = false
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const dataProvider = useDataProvider();
  const [loading, setLoading] = useState(false);
  const [record, setRecord] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [files, setFiles] = useState([]);

  const incidentTypes = [
    { id: 'ACCIDENT', name: 'Accident' },
    { id: 'BREAKDOWN', name: 'Breakdown' },
    { id: 'DAMAGE', name: 'Damage' },
    { id: 'THEFT', name: 'Theft' },
    { id: 'VANDALISM', name: 'Vandalism' },
    { id: 'FIRE', name: 'Fire' },
    { id: 'FLOOD', name: 'Flood' },
    { id: 'OTHER', name: 'Other' }
  ];

  // Load drivers when dialog opens
  useEffect(() => {
    if (open) {
      dataProvider.getList('drivers', {
        pagination: { page: 1, perPage: 1000 },
        sort: { field: 'id', order: 'ASC' },
        filter: {}
      })
        .then(({ data }) => {
          const driverChoices = data.map(driver => ({
            id: driver.id,
            name: driver.user?.name || `Driver ${driver.id}`
          }));
          setDrivers(driverChoices);
        })
        .catch((error) => {
          console.error('Error loading drivers:', error);
          notify('Error loading drivers', { type: 'error' });
        });
    }
  }, [open, dataProvider, notify]);

  // Load record for edit mode
  useEffect(() => {
    if (isEdit && recordId && open) {
      setLoading(true);
      dataProvider.getOne('equipment-incidents', { id: recordId })
        .then(({ data }) => {
          setRecord(data);
          // Load files if they exist
          if (data.files) {
            setFiles(data.files);
          }
        })
        .catch((error) => {
          console.error('Error loading incident record:', error);
          notify('Error loading incident record', { type: 'error' });
        })
        .finally(() => {
          setLoading(false);
        });
    } else if (!isEdit) {
      setRecord(null);
      setFiles([]);
    }
  }, [isEdit, recordId, open, dataProvider, notify]);

  const handleSubmit = async (data: any) => {
    setLoading(true);
    try {
      const formattedData = {
        ...data,
        equipmentId: Number(equipmentId),
        files: files, // Include files in the submission
      };
      
      console.log('Submitting incident data:', formattedData);
      
      if (isEdit && recordId) {
        await dataProvider.update('equipment-incidents', {
          id: recordId,
          data: formattedData,
          previousData: record || {}
        });
        notify('Incident record updated successfully', { type: 'success' });
      } else {
        await dataProvider.create('equipment-incidents', {
          data: formattedData
        });
        notify('Incident record created successfully', { type: 'success' });
      }
      
      refresh();
      onClose();
    } catch (error) {
      console.error('Incident submission error:', error);
      notify(`Error ${isEdit ? 'updating' : 'creating'} incident record: ${error.message || 'Unknown error'}`, { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ position: 'relative', pr: 6 }}>
        {isEdit ? 'Edit Incident' : 'Add Incident Report'}
        <IconButton onClick={onClose} size="small" sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <SimpleForm 
          onSubmit={handleSubmit} 
          record={record || { 
            incidentDate: new Date().toISOString().split('T')[0] 
          }}
          toolbar={<IncidentToolbar />}
          sx={{
            '& .RaSimpleForm-input': { width: '100%', maxWidth: '100%' },
            '& .MuiFormControl-root': { marginBottom: 0, width: '100%' }
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
            <DateInput source="incidentDate" validate={[required()]} label="Incident Date" variant="outlined" size="small" fullWidth />
            <SelectInput source="incidentType" choices={incidentTypes} validate={[required()]} label="Incident Type" variant="outlined" size="small" fullWidth />
            <TextInput source="description" validate={[required()]} multiline rows={3} label="Description" variant="outlined" size="small" fullWidth />
            <SelectInput source="driverId" choices={drivers} label="Driver (if applicable)" variant="outlined" size="small" fullWidth />
            <NumberInput source="repairCost" label="Repair Cost ($)" variant="outlined" size="small" fullWidth />
            <BooleanInput source="resolved" label="Resolved" />
            <TextInput source="notes" multiline rows={3} label="Additional Notes" variant="outlined" size="small" fullWidth />
          </Box>
        </SimpleForm>
        
        {/* File Attachments Section */}
        {isEdit && recordId && (
          <Box sx={{ mt: 3 }}>
            <EquipmentFileUpload
              entityType="incident"
              entityId={recordId}
              files={files}
              onFilesChange={setFiles}
              title="Incident Photos & Reports"
            />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};